import { MapSearch } from './map_search';

class MapSearchAutoComplete {
  AUTOCOMPLETE_MAX_LIMIT = 10;
  AUTOCOMPLETE_MAP_LIMIT = 7;

  constructor(mapSearch, locationSelectedCallback, placeSelectedCallback, googleService, placeService, sessionToken, viewType) {
    this.mapSearch = mapSearch;
    this.locations = mapSearch.locations;
    this.locationSelectedCallback = locationSelectedCallback;
    this.placeSelectedCallback = placeSelectedCallback;
    this.googleService = googleService;
    this.placeService = placeService;
    this.sessionToken = sessionToken;
    this.viewType = viewType;
    this.buildListeners();
  }

  buildListeners() {
    this.activateSearchingListener();
    document.addEventListener('click', (event) => {
      if (!event.target.closest('.autocomplete')) {
        this.removeAutoCompleteFields();
      }
    });
    const searchInput = document.getElementById('search');
    if (searchInput) {
      searchInput.addEventListener('focus', (event) => this.buildAutoComplete(event.target.value));
      searchInput.addEventListener('keydown', (event) => {
        if (event.keyCode === 13) {
          if (this.viewType === 'list') {
            this.mapSearch.runFilter();
          } else {
            this.activeFirstSuitableLocation();
          }
        }
      });
    }
  }

  activateSearchingListener() {
    let waitInputCallback = null;
    const searchInput = document.getElementById('search');
    if (searchInput) {
      searchInput.addEventListener('input', (event) => {
        const callback = () => {
          waitInputCallback = null;
          this.buildAutoComplete(event.target.value);
        };
        if (!waitInputCallback) {
          waitInputCallback = setTimeout(callback, 200);
        }
      });
    }
  }

  activeFirstSuitableLocation() {
    const searchValue = document.getElementById('search')?.value || '';
    const location = this.containsLocation(searchValue);
    const locationElement = location === null ? document.querySelector('.autocomplete-items div') : location.parentNode;
    if (!locationElement) {
      return;
    }
    this.autoCompleteClicked(locationElement);
    this.removeAutoCompleteFields();
  }

  containsLocation(searchedText) {
    const locationNames = document.querySelectorAll('.location-name');
    const lowerSearchText = searchedText.toLowerCase();
    for (const location of locationNames) {
      if (location.innerHTML.toLowerCase() === lowerSearchText) {
        return location;
      }
    }
    return null;
  }

  autoCompleteClicked(clickedField) {
    this.removeAutoCompleteFields();
    const searchInput = document.getElementById('search');
    const locationName = clickedField.querySelector('.location-name')?.innerHTML;
    if (searchInput && locationName) {
      searchInput.value = locationName;
    }
    if (clickedField.dataset.exact === 'true') {
      this.locationSelectedCallback(clickedField.dataset.locationId, clickedField.dataset.latitude, clickedField.dataset.longitude);
    } else {
      this.placeService.getDetails(
        { placeId: clickedField.dataset.placeId, fields: ['geometry'] },
        (place, status) => {
          if (status !== google.maps.places.PlacesServiceStatus.OK) {
            return;
          }
          this.placeSelectedCallback(place.geometry.location);
        }
      );
    }
  }

  buildAutoComplete(searchedText) {
    this.removeAutoCompleteFields();
    if (searchedText === '') {
      return [];
    }
    const suitableLocations = this.findSuitableLocations(searchedText);
    this.initializeFields(searchedText, suitableLocations);
  }

  findSuitableLocations(searchedText) {
    const preparedFilters = this.mapSearch.prepareFilters();
    const foundLocations = [];
    this.locations.forEach(location => {
      if (MapSearch.searching(location, searchedText) && this.mapSearch.runChecking(preparedFilters, location)) {
        foundLocations.push(location);
      }
    });
    return foundLocations.slice(0, this.bikeLinkLocationLimit(foundLocations.length));
  }

  bikeLinkLocationLimit(numberOfLocations) {
    if (numberOfLocations >= this.AUTOCOMPLETE_MAP_LIMIT) {
      return this.AUTOCOMPLETE_MAP_LIMIT;
    }
    return numberOfLocations;
  }

  initializeFields(searchedText, suitableLocations) {
    const googleParams = {
      input: searchedText,
      sessionToken: this.sessionToken,
      componentRestrictions: { country: 'us' }
    };
    this.googleService.getPlacePredictions(googleParams, (predictions, status) => {
      this.initializeFieldsWithGooglePredictions(suitableLocations, predictions, status);
    });
  }

  initializeFieldsWithGooglePredictions(suitableLocations, predictions, status) {
    this.addBikeLinkLocations(suitableLocations);
    if (suitableLocations.length !== this.AUTOCOMPLETE_MAX_LIMIT) {
      this.addGoogleLocations(suitableLocations, predictions, status);
    }
    const autocompleteItems = document.querySelector('.autocomplete-items');
    if (autocompleteItems) {
      autocompleteItems.style.display = 'inline-table';
      const items = autocompleteItems.querySelectorAll('div');
      items.forEach(item => {
        item.addEventListener('click', () => this.autoCompleteClicked(item));
      });
    }
  }

  addBikeLinkLocations(suitableLocations) {
    suitableLocations.forEach(location => {
      this.buildAutoCompleteField(
        this.iconOf(location),
        location.human_name,
        { locationId: location.id, latitude: location.latitude, longitude: location.longitude, exact: true }
      );
    });
  }

  addGoogleLocations(suitableLocations, predictions, status) {
    if (status !== google.maps.places.PlacesServiceStatus.OK) {
      return;
    }
    let limit = this.AUTOCOMPLETE_MAX_LIMIT - suitableLocations.length;
    predictions.forEach(prediction => {
      if (limit === 0) {
        return;
      }
      const displayName = prediction.terms.slice(0, -1).map(t => t.value).join(', ');
      this.buildAutoCompleteField('', displayName, { placeId: prediction.place_id, exact: false });
      limit -= 1;
    });
    this.addGoogleLogo();
  }

  addGoogleLogo() {
    const logoField = document.createElement('span');
    logoField.className = 'd-flex flex-row google-logo';
    const autocompleteItems = document.querySelector('.autocomplete-items');
    if (autocompleteItems) {
      autocompleteItems.appendChild(logoField);
    }
  }

  buildAutoCompleteField(iconStyle, name, dataset) {
    const textField = document.createElement('div');
    textField.className = 'd-flex flex-row';
    Object.entries(dataset).forEach(([key, value]) => {
      textField.dataset[key] = value;
    });
    textField.innerHTML = `<span class='icon ${iconStyle}'></span><span class='location-name pl-3 flex-grow-1 align-self-center'>${name}</span>`;
    const autocompleteItems = document.querySelector('.autocomplete-items');
    if (autocompleteItems) {
      autocompleteItems.appendChild(textField);
    }
  }

  removeAutoCompleteFields() {
    const autocompleteItems = document.querySelector('.autocomplete-items');
    if (!autocompleteItems) {
      return;
    }
    const divs = autocompleteItems.querySelectorAll('div');
    const logos = autocompleteItems.querySelectorAll('.google-logo');
    this.removeItems(divs);
    this.removeItems(logos);
    autocompleteItems.style.display = 'none';
  }

  removeItems(items) {
    items.forEach(item => {
      if (item.parentNode) {
        item.parentNode.removeChild(item);
      }
    });
  }

  iconOf(location) {
    const style = {
      'eLocker': 'locker',
      'Group Parking': 'group',
      'Bike Hangar': 'hangar',
      'eRack': 'erack',
      'Vendor': 'vendor'
    };
    return style[location.location_friendly_type];
  }
}

export { MapSearchAutoComplete };
